import {
  InsuranceName,
  VehicleModelType,
  VehicleModelWithInsurancesForD2CDto,
} from '@orcar/common';
import {
  FilterOptionsType,
  includesOptionV2,
  INSURANCE_TYPE,
} from '@orcar/common-d2c';
import { getDefaultFilterOptions } from './filterOptions';
/**
 *
 * @param data GET /vehicleModels API 응답 데이터
 * @param filterOptions 필터링에 사용되는 옵션들
 * @param recoilInsurance 적용된 보험 필터
 * @returns 필터링을 적용한
 */
export const getFilteredSearchResult = (
  data: VehicleModelWithInsurancesForD2CDto[] | undefined,
  filterOptions: FilterOptionsType,
  recoilInsurance: string[],
) => {
  const defaultFilterOptions = getDefaultFilterOptions(data);

  const minPrice = filterOptions.minPrice || defaultFilterOptions.minPrice;
  const maxPrice = filterOptions.maxPrice || defaultFilterOptions.maxPrice;
  const minYear = filterOptions.minYear || defaultFilterOptions.minYear;
  const maxYear = filterOptions.maxYear || defaultFilterOptions.maxYear;
  const carTypeList =
    filterOptions.carTypeList.length === 0
      ? defaultFilterOptions.carTypeList
      : filterOptions.carTypeList;
  const carFuelList =
    filterOptions.carFuelList.length === 0
      ? defaultFilterOptions.carFuelList
      : filterOptions.carFuelList;
  const optionList =
    filterOptions.optionList.length === 0
      ? defaultFilterOptions.optionList
      : filterOptions.optionList;
  const insuranceList =
    recoilInsurance?.length === 0
      ? defaultFilterOptions.insuranceList
      : recoilInsurance;

  return data?.reduce(
    (
      filteredVehicleModelList:
        | VehicleModelWithInsurancesForD2CDto[]
        | undefined,
      currentVehicleModel: VehicleModelWithInsurancesForD2CDto,
    ) => {
      const filteredInsurances = currentVehicleModel.insurances.filter(
        (insurance) => {
          if (
            insuranceList.length === 1 &&
            insuranceList.includes(INSURANCE_TYPE.NORMAL)
          ) {
            return currentVehicleModel.type === VehicleModelType.SUPERCAR; // 보험필터가 일반자차만 선택되어 있으면, 슈퍼카만 노출
          } else {
            if (currentVehicleModel.type === VehicleModelType.SUPERCAR) {
              return insuranceList.includes(INSURANCE_TYPE.NORMAL); // 슈퍼카는 일반자차만 노출
            } else {
              if (insurance.insuranceName === INSURANCE_TYPE.NORMAL) {
                return false; // 슈퍼카가 아닌 경우 일반자차는 제외
              } else {
                return insuranceList.includes(insurance.insuranceName);
              }
            }
          }
        },
      );

      const hasFuel = carFuelList.includes(currentVehicleModel.fuel);
      const hasType = carTypeList.includes(currentVehicleModel.type);
      const hasOption = optionList?.every((option: string) => {
        return includesOptionV2({
          modelOption: currentVehicleModel.modelOption,
          keyOption: currentVehicleModel.keyOption,
          audioOption: currentVehicleModel.audio,
          optionLabel: option,
        });
      });
      const isBelowMaxYearFilter = currentVehicleModel.maxModelYear <= maxYear;
      const isAboveMinYearFilter = currentVehicleModel.minModelYear >= minYear;

      const isHappyWithPriceFilter = currentVehicleModel.insurances
        .filter(
          (insurance) =>
            insurance.insuranceName === INSURANCE_TYPE.PERFECT ||
            insurance.insuranceName === INSURANCE_TYPE.SUPER,
        )
        .every((vehicleModel) => {
          const isBelowMaxPriceFilter =
            (vehicleModel.userFinalPrice || vehicleModel.finalPrice) <=
            maxPrice;
          const isAboveMinPriceFilter =
            (vehicleModel.userFinalPrice || vehicleModel.finalPrice) >=
            minPrice;
          return isBelowMaxPriceFilter && isAboveMinPriceFilter;
        });

      if (
        !hasFuel ||
        !hasType ||
        !hasOption ||
        !isAboveMinYearFilter ||
        !isBelowMaxYearFilter ||
        !isHappyWithPriceFilter ||
        filteredInsurances.length === 0
      )
        return filteredVehicleModelList;
      return filteredVehicleModelList?.concat({
        ...currentVehicleModel,
        insurances: filteredInsurances,
      });
    },
    [],
  );
};

export function getMaxFinalPrice(
  vehicleModelList?: VehicleModelWithInsurancesForD2CDto[],
) {
  if (!vehicleModelList) return 0;

  let maxPrice = 0;

  for (const vehicleModel of vehicleModelList) {
    if (vehicleModel.availableCount !== 0) {
      for (const insurance of vehicleModel.insurances) {
        if ((insurance.userFinalPrice || insurance.finalPrice) > maxPrice) {
          if (vehicleModel.type === VehicleModelType.SUPERCAR) {
            if (insurance.insuranceName === InsuranceName.Normal) {
              maxPrice = insurance.userFinalPrice || insurance.finalPrice;
            }
          } else {
            if (
              insurance.insuranceName === InsuranceName.Perfect ||
              insurance.insuranceName === InsuranceName.Super
            ) {
              maxPrice = insurance.userFinalPrice || insurance.finalPrice;
            }
          }
        }
      }
    }
  }

  return maxPrice;
}
